import {
  addCompanyBadge,
  addDepartmentBadge,
  addUserBadge,
  getBadges,
} from "../repository/data.repository";
import { createSearchRequest } from "../hooks/useSearchRequest";
import { Badge } from "../repository/data.default";
import { Dayjs } from "dayjs";
import AssignEntityFormDialog from "./AssignEntityFormDialog";

export interface AssignBadgeFormDialogProps {
  readonly open?: boolean;
  readonly initialValues?: Badge;
  readonly target?: {
    readonly type: "user";
    readonly uid: string;
  };
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// Thin wrapper over the shared AssignEntityFormDialog: a badge picker + assignment/expiration dates,
// fanned out to the company/department/user badge-assignment endpoints.
function AssignBadgeFormDialog(props: AssignBadgeFormDialogProps) {
  return (
    <AssignEntityFormDialog
      open={props.open}
      title="Assign Badge"
      entityField="badge"
      initialEntityValues={
        props.initialValues
          ? {
              badge: {
                display: props.initialValues.name,
                value: props.initialValues.uid,
              },
            }
          : undefined
      }
      target={props.target}
      entityControls={[
        {
          type: "combobox",
          label: "Badge",
          name: "badge",
          request: createSearchRequest(getBadges, "name"),
          rules: [{ required: true, message: "Badge is required" }],
        },
        {
          type: "datepicker",
          label: "Assignment Date",
          name: "assignmentDate",
          size: "w-3/6",
          rules: [{ required: true, message: "Assignment Date is required" }],
        },
        {
          type: "datepicker",
          label: "Expiration Date",
          name: "expirationDate",
          size: "w-3/6",
          rules: [
            ({ getFieldValue }) => ({
              validator(_, expirationDate) {
                const assignmentDate = getFieldValue("assignmentDate");
                if (
                  !expirationDate ||
                  !assignmentDate ||
                  assignmentDate < expirationDate
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "Expiration date cannot be less than assignment date",
                  ),
                );
              },
            }),
          ],
        },
      ]}
      canSubmit={(v) => !!(v?.badge?.value && v?.assignmentDate)}
      assignFns={{
        company: (companyUid, v) =>
          addCompanyBadge({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            badgeUid: v.badge.value,
            companyUid,
          }),
        department: (departmentUid, v) =>
          addDepartmentBadge({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            badgeUid: v.badge.value,
            departmentUid,
          }),
        user: (userUid, v) =>
          addUserBadge({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            badgeUid: v.badge.value,
            userUid,
          }),
      }}
      onFinish={props.onFinish}
      onCancel={props.onCancel}
    />
  );
}

export default AssignBadgeFormDialog;
